import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 10 }, (_, i) => CURRENT_YEAR - i);

const MONTHS = [
  { value: "01", label: "Janeiro" },
  { value: "02", label: "Fevereiro" },
  { value: "03", label: "Março" },
  { value: "04", label: "Abril" },
  { value: "05", label: "Maio" },
  { value: "06", label: "Junho" },
  { value: "07", label: "Julho" },
  { value: "08", label: "Agosto" },
  { value: "09", label: "Setembro" },
  { value: "10", label: "Outubro" },
  { value: "11", label: "Novembro" },
  { value: "12", label: "Dezembro" },
];

export default function BackupDownloadModal({ open, onClose, onDownload }) {
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR.toString());
  const [selectedMonth, setSelectedMonth] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [processingMessage, setProcessingMessage] = useState("");

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
    setError("");
    setProcessingMessage("");
  };

  const handleMonthChange = (event) => {
    setSelectedMonth(event.target.value);
    setError("");
    setProcessingMessage("");
  };

  const isFormValid = () => {
    if (!selectedYear) {
      return false;
    }

    if (!selectedMonth) {
      return false;
    }

    return true;
  };

  const handleDownload = async () => {
    if (!isFormValid()) {
      setError("Por favor, selecione o ano e o mês");
      return;
    }

    setLoading(true);
    setError("");
    setProcessingMessage("");

    try {
      const response = await onDownload(selectedYear, selectedMonth);

      if (response.status === "ready") {
        handleDirectDownload(response.download_url);
        handleClose();
      }

      if (response.status === "processing") {
        setProcessingMessage(
          response.message ||
            "Backup solicitado. Aguarde alguns minutos enquanto processamos sua solicitação."
        );
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Erro ao baixar o backup. Tente novamente.";

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDirectDownload = (url) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = `backup_${selectedYear}_${selectedMonth}.zip`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClose = () => {
    if (loading) {
      return;
    }

    setSelectedYear(CURRENT_YEAR.toString());
    setSelectedMonth("");
    setError("");
    setProcessingMessage("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Baixar Backup de Notas Fiscais</DialogTitle>

      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          {processingMessage && (
            <Alert severity="info" onClose={() => setProcessingMessage("")}>
              {processingMessage}
            </Alert>
          )}

          <TextField
            select
            fullWidth
            label="Ano"
            value={selectedYear}
            onChange={handleYearChange}
            disabled={loading}
          >
            {YEARS.map((year) => (
              <MenuItem key={year} value={year.toString()}>
                {year}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            label="Mês"
            value={selectedMonth}
            onChange={handleMonthChange}
            disabled={loading}
          >
            {MONTHS.map((month) => (
              <MenuItem key={month.value} value={month.value}>
                {month.label}
              </MenuItem>
            ))}
          </TextField>

          {processingMessage && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Você pode fechar esta janela. O backup estará disponível em
                alguns minutos. Tente novamente mais tarde para baixar o
                arquivo.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleDownload}
          variant="contained"
          disabled={loading || !isFormValid()}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? "Processando..." : "Baixar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
